import { motion } from 'framer-motion';
import { getIndustryBenchmark } from '../../../engine/benchmark';
import { carbonRegistry } from '../../../engine/carbonRegistryAdapter';
import { useCompanyStore } from '../stores/company';
import { generateBankPayload, exportBankPayload } from '../utils/BankExport';
import { useTranslation } from 'react-i18next';
import ResultsChart from './results/ResultsChart';
import ResultsActions from './results/ResultsActions';


type ResultsViewProps = {
  baseline: { energyKWh: number; fleetLiter: number; travelKm: number; wasteKg: number; paperQty: number; totalCo2Kg: number };
  optimized: { energyKWh: number; fleetLiter: number; travelKm: number; wasteKg: number; paperQty: number; totalCo2Kg: number };
  companyName?: string;
  industry?: string;
  employees?: number;
};

export function ResultsView({ baseline, optimized, companyName, industry = 'it_services', employees = 100 }: ResultsViewProps) {
  const { t } = useTranslation();
  const activeCompany = useCompanyStore((s) => s.companies.find((c) => c.id === s.activeId) ?? null);
  const data = [
    { name: 'Energy', Baseline: baseline.energyKWh, Optimized: optimized.energyKWh },
    { name: 'Fleet', Baseline: baseline.fleetLiter, Optimized: optimized.fleetLiter },
    { name: 'Travel', Baseline: baseline.travelKm, Optimized: optimized.travelKm },
    { name: 'Waste', Baseline: baseline.wasteKg, Optimized: optimized.wasteKg },
    { name: 'Paper', Baseline: baseline.paperQty, Optimized: optimized.paperQty }
  ];

  const bench = getIndustryBenchmark({ industry, employees });
  const co2PerEmployee = employees > 0 ? (baseline.totalCo2Kg / employees) : 0;
  const diffPct = bench.avgKgCo2PerEmployee > 0 ? ((co2PerEmployee - bench.avgKgCo2PerEmployee) / bench.avgKgCo2PerEmployee) * 100 : 0;
  const better = diffPct < 0;
  const statement = better
    ? `Your company is ${Math.abs(diffPct).toFixed(1)}% better than industry average.`
    : `Your company is ${Math.abs(diffPct).toFixed(1)}% worse than industry average.`;

  const saved = Math.max(0, baseline.totalCo2Kg - optimized.totalCo2Kg);
  async function retire() {
    const regs = await carbonRegistry.listApprovedRegistries();
    const reg = regs[0];
    const cert = await carbonRegistry.createOffsetCertificate(reg.id, saved);
    const tx = await carbonRegistry.retireCredit(cert.id);
    // eslint-disable-next-line no-alert
    alert(`Retired ${saved.toFixed(2)} kg via ${reg.name}. Cert=${cert.id}, tx=${tx.txId}`);
  }

  function exportBank() {
    const payload = generateBankPayload({
      companyId: activeCompany?.id || 'unknown',
      esg_score: better ? Math.min(100, 70 + Math.abs(diffPct) / 2) : Math.max(0, 70 - Math.abs(diffPct) / 2),
      co2_reduction: saved,
      industry_rating: Math.max(0, Math.min(100, 100 - Math.abs(diffPct))),
      risk_vector: { transition: better ? 0.2 : 0.6, physical: 0.4, compliance: better ? 0.2 : 0.5 }
    });
    exportBankPayload(`bank_export_${activeCompany?.id || 'company'}.json`, payload);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-4 border border-evat-neon/50 rounded bg-black/50">
      <div className="font-orbitron text-evat-neon mb-2">Comparative Results{companyName ? ` • ${companyName}` : ''}</div>
      <ResultsChart data={data} />
      <div className={`mt-3 text-sm ${better ? 'text-emerald-400' : 'text-red-400'}`}>{statement}</div>
      <ResultsActions savedKg={saved} onRetire={retire} onExportBank={exportBank} />
    </motion.div>
  );
}

export default ResultsView;


